import { defineStore } from 'pinia';
import { toast } from 'vue3-toastify';
import {
    MetaOnboardingAoVivoAdminService,
    pesoDoItem,
    type ItemAoVivo,
} from '@/services/meta-trilha/meta-onboarding-ao-vivo.service';

/** Encerradas somem do widget depois disto (o server também corta em 30 min). */
const MINUTOS_RECENTE = 30;
const POLLING_MS = 5_000;
const RETENTAR_SSE_MS = 30_000;

type Modo = 'desligado' | 'conectando' | 'sse' | 'polling';

/**
 * Onboarding Meta ao vivo no admin. Uma conexão SSE para a aba inteira, com
 * contagem de referências: o widget é remontado a cada troca de página
 * (`AdminPage`), e reabrir o SSE a cada navegação perderia eventos e
 * multiplicaria conexões.
 *
 * SSE caiu (cookie expirado, proxy) → polling a cada 5 s pelo `FetchUtil`, que
 * renova o token; a cada 30 s tenta voltar ao SSE.
 */
export const useOnboardingAoVivoStore = defineStore('admin-onboarding-ao-vivo', {
    state: () => ({
        itens: {} as Record<string, ItemAoVivo>,
        modo: 'desligado' as Modo,
        ultimaAtualizacao: null as string | null,
        referencias: 0,
    }),

    getters: {
        lista(state): ItemAoVivo[] {
            const limite = Date.now() - MINUTOS_RECENTE * 60_000;
            return Object.values(state.itens)
                .filter((i) => i.status === 'EM_ANDAMENTO' || i.wame?.situacao === 'AGUARDANDO_ROTEAMENTO' || i.motivos?.includes('OPERADOR')
                    || (i.encerradoEm && new Date(i.encerradoEm).getTime() >= limite))
                .sort((a, b) => pesoDoItem(a) - pesoDoItem(b) || b.ultimaAtividadeEm.localeCompare(a.ultimaAtividadeEm));
        },
        contagem(): { andamento: number; roteamento: number; falhas: number; operador: number } {
            const l = this.lista as ItemAoVivo[];
            return {
                andamento: l.filter((i) => i.status === 'EM_ANDAMENTO').length,
                roteamento: l.filter((i) => i.wame?.situacao === 'AGUARDANDO_ROTEAMENTO').length,
                falhas: l.filter((i) => i.status === 'FALHOU' || i.status === 'ABANDONADA').length,
                operador: l.filter((i) => i.motivos?.includes('OPERADOR')).length,
            };
        },
    },

    actions: {
        iniciar() {
            this.referencias++;
            if (this.referencias > 1) return;
            void this.carregar();
            this.abrirSse();
        },

        parar() {
            this.referencias = Math.max(0, this.referencias - 1);
            if (this.referencias > 0) return;
            fecharTudo();
            this.modo = 'desligado';
        },

        async carregar() {
            try {
                const lista = await servico.snapshot();
                const novo: Record<string, ItemAoVivo> = {};
                for (const i of lista) novo[i.idExecucao] = i;
                this.itens = novo;
                this.ultimaAtualizacao = new Date().toISOString();
            } catch (e) {
                console.warn('[onboarding-ao-vivo] snapshot falhou', e);
            }
        },

        aplicar(item: ItemAoVivo) {
            const anterior = this.itens[item.idExecucao];
            this.itens = { ...this.itens, [item.idExecucao]: item };
            this.ultimaAtualizacao = new Date().toISOString();
            // Transição para falha é o que o admin precisa ver mesmo sem olhar o widget.
            if (item.status === 'FALHOU' && anterior?.status !== 'FALHOU') {
                toast.error(`Onboarding falhou: ${item.nomeInbox || item.nomeConta || item.idExecucao} — ${item.desfechoResumo ?? item.etapaDecisiva ?? ''}`, { autoClose: 8000 });
            }
        },

        abrirSse() {
            fecharTudo();
            if (typeof EventSource === 'undefined') return this.irParaPolling();
            this.modo = 'conectando';
            try {
                fonte = new EventSource(servico.urlStream(), { withCredentials: true });
            } catch {
                return this.irParaPolling();
            }
            fonte.onmessage = (ev) => {
                let dado: { tipo?: string; item?: ItemAoVivo } = {};
                try { dado = JSON.parse(ev.data); } catch { return; }
                if (dado.tipo === 'CONECTADO') {
                    this.modo = 'sse';
                    // Pode ter perdido eventos entre o retrato e a abertura.
                    void this.carregar();
                } else if (dado.tipo === 'ITEM' && dado.item) {
                    this.aplicar(dado.item);
                }
            };
            fonte.onerror = () => {
                // O EventSource reconecta sozinho, mas em 401 fica em loop: cai para polling.
                this.irParaPolling();
            };
        },

        irParaPolling() {
            fecharTudo();
            this.modo = 'polling';
            timerPolling = setInterval(() => void this.carregar(), POLLING_MS);
            timerRetentar = setTimeout(() => { if (this.referencias > 0) this.abrirSse(); }, RETENTAR_SSE_MS);
        },
    },
});

const servico = new MetaOnboardingAoVivoAdminService();
let fonte: EventSource | null = null;
let timerPolling: ReturnType<typeof setInterval> | undefined;
let timerRetentar: ReturnType<typeof setTimeout> | undefined;

function fecharTudo() {
    fonte?.close();
    fonte = null;
    if (timerPolling) clearInterval(timerPolling);
    if (timerRetentar) clearTimeout(timerRetentar);
    timerPolling = undefined;
    timerRetentar = undefined;
}
